import { createApi } from './core';
import { Pod, PodAddProviderRequest, PodCreateRequest, PodProvider, PodSearchResult, PodUpdateProviderRequest, PodUpdateRequest } from './pods.types';
import { ApiErrorResponse } from 'apisauce';

declare const healConfig: any;

const errors = {
  POD_NOT_FOUND: 'The specified pod does not exist.',
  POD_PROVIDER_NOT_FOUND: 'The specified provider does not exist in the pod.',
  UNABLE_ADD_PROVIDER: 'Unable to add the provider.',
  UNABLE_CREATE: 'Unable to create the pod.',
  UNABLE_QUERY: 'An error occurred while searching for pods.',
  UNABLE_RETRIEVE: 'Unable to load the pod.',
  UNABLE_UPDATE_POD: 'Unable to update the pod.',
  UNABLE_UPDATE_PROVIDER: 'Unable to update the provider.',
  UNKNOWN_ERROR: 'An unknown error occurred.',
}

function createPodsApi() {
  return createApi({
    baseURL: healConfig.podServiceApiBaseUrl,
    hideMessage: true,
  });
}

type PodServerError = { message?: string, diagnostic?: string };
type PodApiErrorResponse = ApiErrorResponse<PodServerError>;

/**
 * Generate an error to send back to the method caller. If possible, use the `message`
 * and `diagnostic` fields typically included with API error responses. If these aren't
 * provided, fall back to defaultMessage. If defaultMessage isn't provided, message will
 * be undefined, which allows us to conditionally hide the debug information section of
 * the placeholder.
 */
const handleError = (response: PodApiErrorResponse, defaultMessage?: string) => {
  // Error from API
  if (response.data?.hasOwnProperty('diagnostic')) {
    return Promise.reject({
      status: response.status,
      message: response.data.message,
      diagnostic: response.data.diagnostic,
    });
  }


  return Promise.reject({
    status: response.status,
    message: defaultMessage,
  });
}

/**
 * Fetch the list of pods, optionally including a string by which to filter.
 * @param [query] A string by which to filter pod results.
 * @returns A list of the pods matching the query, if provided, or all pods otherwise.
 */
export function getPodsByQuery(query?: string): Promise<PodSearchResult[]> {
  return createPodsApi()
    .then(api => api.get<PodSearchResult[]>('/pods', { keyword: query }))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else {
        return handleError(r as PodApiErrorResponse);
      }
    });
}

/**
 * Fetch the list of pods to which a given providerId is assigned.
 * @param providerId A string containing the providerId for which to search.
 * @returns A list of the pods to which the provider is assigned, if any.
 */
 export function getPodsByProviderId(providerId: string): Promise<PodSearchResult[]> {
  return createPodsApi()
    .then(api => api.get<PodSearchResult[]>('/pods', { providerId }))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else {
        return handleError(r as PodApiErrorResponse);
      }
    });
}

/**
 * Fetch a pod by its ID.
 * @param id The ID of the pod to retrieve.
 * @returns The data of the specified pod.
 */
export function getPodById(id: Pod['id']): Promise<Pod> {
  return createPodsApi()
    .then(api => api.get<Pod>(`/pods/${encodeURIComponent(id)}`))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else if (r.status === 404) {
        return Promise.reject({
          status: 404,
          message: errors.POD_NOT_FOUND,
        });
      } else {
        return handleError(r as PodApiErrorResponse);
      }
    })
}

/**
 * Make a request to create a pod.
 * @param data The data of the pod to create.
 * @returns The data of the newly created pod.
 */
export function createPod(data: PodCreateRequest) {
  return createPodsApi()
    .then(api => api.post<Pod>('/pods', data))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else {
        return handleError(r as PodApiErrorResponse, errors.UNABLE_CREATE)
      }
    });
}

/**
 * Make a request to add a provider to a pod.
 * @param podId The ID of the pod to which to add the provider.
 * @param data The data of the provider to add.
 * @returns The data of the newly added provider.
 */
export function addPodProvider(podId: Pod['id'], data: PodAddProviderRequest) {
  return createPodsApi()
    .then(api => api.post<PodProvider>(`/pods/${encodeURIComponent(podId)}/providers`, data))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else if (r.status === 404) {
        return Promise.reject({
          status: 404,
          message: errors.POD_NOT_FOUND
        });
      } else {
        return handleError(r as PodApiErrorResponse, errors.UNABLE_ADD_PROVIDER);
      }
    });
}

/**
 * Make a request to update a pod.
 * @param podId The ID of the pod to update.
 * @param data The new data to store for the pod.
 * @returns The data of the updated pod.
 */
export function updatePod(podId: Pod['id'], data: PodUpdateRequest) {
  return createPodsApi()
    .then(api => api.put<Pod>(`/pods/${encodeURIComponent(podId)}`, data))
    .then(r => {
      if (r.ok && r.data) {
        return r.data;
      } else if (r.status === 404) {
        return Promise.reject({
          status: 404,
          message: errors.POD_NOT_FOUND
        });
      } else {
        return handleError(r as PodApiErrorResponse, errors.UNABLE_UPDATE_POD);
      }
    })
}

/**
 * Make a request to update a provider in a pod.
 * @param podId The ID of the pod to search.
 * @param podProviderId The unique ID of this provider entry in the podProvider table.
 * Note: This is different from the providerId, which may not be unique if the provider was
 * added, deactivated, then added again; or if they are assigned to multiple pods, as is the
 * case with PCCs and PSCs.
 * @param data The new data to store for the provider.
 * @returns The data of the updated provider.
 */
export function updatePodProvider(podId: Pod['id'], podProviderId: PodProvider['id'], data: PodUpdateProviderRequest) {
  return createPodsApi()
    .then(api => api.put<PodProvider>(`pods/${encodeURIComponent(podId)}/providers/${encodeURIComponent(podProviderId)}`, data))
    .then(r => {
      if (r.ok) {
        return;
      } else if (r.status === 404) {
        return Promise.reject({
          status: 404,
          message: errors.POD_PROVIDER_NOT_FOUND
        });
      } else {
        return handleError(r as PodApiErrorResponse, errors.UNABLE_UPDATE_PROVIDER);
      }
    })
}